import { getLoggedInPersonId } from "components/helpers/AuthHelpers";
import { isNullOrEmpty, isObjectEmpty } from "components/helpers/ObjectHelpers";
import SourceApiRepository from "repositories/api/SourceApiRepository";
import { actorTypeToDisplayName, IncidentHeaderDialogStates, IncidentStatuses } from "./DataHelpers";
import { PropertyManager } from "./PropertyManager";
import Moment from "moment";

function getIncidentShipNameByJson(data) {
    const ship = data.find((s) => s.isMainShip === true);
    if (isNullOrEmpty(ship)) return "";

    return getShipName(ship);
}

function getIncidentTime(incident) {
    if (isNullOrEmpty(incident)) return "";
    if (isNullOrEmpty(incident.incidentTime)) return "";
    return Moment(incident.incidentTime).format("DD.MM.yyyy HH:mm");
}

function getIncidentMenuItems(isCaseworker, incidentDetails, showValidation) {
    const pilotPdfItem = { name: "Losrapport", state: IncidentHeaderDialogStates.PilotPdf };
    const incidentPdfItem = { name: "Sluttrapport", state: IncidentHeaderDialogStates.Pdf };
    const deleteItem = { name: "Slett hendelsen", state: IncidentHeaderDialogStates.Delete };
    const correctionItem = { name: "Send til korrigering", state: IncidentHeaderDialogStates.PreviousStatus };
    const awaitingItem = { name: "Send til saksbehandling", state: IncidentHeaderDialogStates.SendToProcessing };
    const processItem = { name: "Start saksbehandling", state: IncidentHeaderDialogStates.NextStatus };
    const completeItem = { name: "Fullfør saksbehandling", state: IncidentHeaderDialogStates.SendToArchive };
    const correctionCaseworkerItem = {
        name: "Korriger hendelse",
        state: IncidentHeaderDialogStates.CorrectionCaseworker,
    };
    const previewPilotPdfItem = { name: "Forhåndsvis losrapport", state: IncidentHeaderDialogStates.GeneratePilotPdf };
    const previewIncidentPdfItem = {
        name: "Forhåndsvis sluttrapport",
        state: IncidentHeaderDialogStates.GeneratePdf,
    };

    const items = [];

    if (isCaseworker) {
        switch (incidentDetails.incidentStatus.systemName) {
            case IncidentStatuses.Draft:
                break;
            case IncidentStatuses.CorrectionNeeded:
                break;
            case IncidentStatuses.AwaitingProcessing:
                items.push(correctionItem);
                items.push(processItem);
                items.push(deleteItem);
                break;
            case IncidentStatuses.BeingProcessed:
                items.push(completeItem);
                items.push(previewIncidentPdfItem);
                if (incidentDetails.isCreatedByPilot) {
                    items.push(pilotPdfItem);
                }
                items.push(deleteItem);
                break;
            case IncidentStatuses.ProcessingFinished:
                items.push(correctionCaseworkerItem);
                if (incidentDetails.isCreatedByPilot) {
                    items.push(pilotPdfItem);
                }
                items.push(incidentPdfItem);
                break;
            case IncidentStatuses.Archived:
                items.push(correctionCaseworkerItem);
                if (incidentDetails.isCreatedByPilot) {
                    items.push(pilotPdfItem);
                }
                items.push(incidentPdfItem);
                break;
            case IncidentStatuses.CorrectionCaseworker:
                items.push(completeItem);
                items.push(previewIncidentPdfItem);
                if (incidentDetails.isCreatedByPilot) {
                    items.push(pilotPdfItem);
                }
                break;
            default:
                if (incidentDetails.isCreatedByPilot) {
                    items.push(pilotPdfItem);
                }
                items.push(incidentPdfItem);
                break;
        }
    } else {
        switch (incidentDetails.incidentStatus.systemName) {
            case IncidentStatuses.Draft:
                items.push(awaitingItem);
                items.push(previewPilotPdfItem);
                items.push(deleteItem);
                break;
            case IncidentStatuses.CorrectionNeeded:
                items.push(awaitingItem);
                items.push(previewPilotPdfItem);
                items.push(deleteItem);
                break;
            case IncidentStatuses.AwaitingProcessing:
                break;
            default:
                break;
        }
    }
    return items;
}

function getShipName(ship) {
    return getObjectPropertyValue(ship, "SHIP_INFORMATION_NAME");
}

function getNavigatorName(navigator) {
    return getObjectPropertyValue(navigator, "NAVIGATOR_NAME", "navigatorDetailsJson");
}
function getNavigatorCountry(navigator) {
    return getObjectPropertyValue(navigator, "NAVIGATOR_NATIONALITY", "navigatorDetailsJson");
}

function getPilotName(e) {
    if (e.hasOwnProperty("firstName") && e.hasOwnProperty("lastName")) {
        return `${e.firstName} ${e.lastName}`;
    }
    if (e.hasOwnProperty("pilotDetailsJson")) {
        const pilotDetails = JSON.parse(e.pilotDetailsJson);
        const detailsPropertyManager = new PropertyManager(pilotDetails.itemProperties);
        return detailsPropertyManager.get("PILOT_NAME");
    }
    return "";
}

function getObjectPropertyValue(obj, propertyName, jsonPropertyName = "json") {
    if (isNullOrEmpty(obj)) return "";

    const jsonObj = JSON.parse(obj[jsonPropertyName]);
    const propertyManager = new PropertyManager(jsonObj.itemProperties);
    return propertyManager.get(propertyName);
}

function isIncidentCreatedByLoggedInPerson(incident) {
    if (isObjectEmpty(incident)) return false;

    return incident.createdByPersonID === getLoggedInPersonId();
}

async function getPositionNameAsync(position) {
    const response = await SourceApiRepository.getPositionAsync(position);
    return await response.json();
}

function isIncidentDraft(incident) {
    return incident.incidentStatus.systemName === IncidentStatuses.Draft;
}

function isIncidentCorrectionNeeded(incident) {
    return incident.incidentStatus.systemName === IncidentStatuses.CorrectionNeeded;
}

function isIncidentAwaitingProcessing(incident) {
    return incident.incidentStatus.systemName === IncidentStatuses.AwaitingProcessing;
}

function isIncidentBeingProcessed(incident) {
    return incident.incidentStatus.systemName === IncidentStatuses.BeingProcessed;
}

function isIncidentCorrectionCaseworker(incident) {
    return incident.incidentStatus.systemName === IncidentStatuses.CorrectionCaseworker;
}

function isIncidentProcessingFinished(incident) {
    return incident.incidentStatus.systemName === IncidentStatuses.ProcessingFinished;
}

function isIncidentArchived(incident) {
    return incident.incidentStatus.systemName === IncidentStatuses.Archived;
}

function getPropertyTitle(systemName) {
    switch (systemName) {
        case "SHIP_INFORMATION":
            return "Registrerte opplysninger";
        case "SHIP_INFORMATION_FLAG_STATE":
            return "Flaggstat";
        case "SHIP_INFORMATION_MANAGEMENT":
            return "Management";
        case "SHIP_INFORMATION_OWNER":
            return "Eier";
        case "SHIP_INFORMATION_IMO":
            return "IMO";
        case "SHIP_INFORMATION_MMSI":
            return "MMSI";
        case "SHIP_INFORMATION_CALLSIGN":
            return "Kjenningssignal";
        case "SHIP_INFORMATION_NAME":
            return "Fartøynavn";
        case "SHIP_INFORMATION_GROSS_TONNAGE":
            return "BT";
        case "SHIP_INFORMATION_LENGTH_LOA":
            return "Lengde";
        case "SHIP_INFORMATION_WIDTH":
            return "Bredde";
        case "SHIP_INFORMATION_DEAD_WEIGHT":
            return "DW";
        case "SHIP_INFORMATION_DRAUGHT_MAX":
            return "Max DPG";
        case "SHIP_INFORMATION_SHIP_TYPE":
            return "Type fartøy";
        case "SHIP_INFORMATION_YEAR_OF_BUILD":
            return "Byggeår";

        case "VOYAGE_INFORMATION":
            return "Rapportert seilas";
        case "VOYAGE_LOCATION_DEPARTURE":
            return "Seilas start lokasjon";
        case "VOYAGE_LOCATION_DEPARTURE_TIME":
            return "Seilas start";
        case "VOYAGE_LOCATION_ARRIVAL":
            return "Seilas stopp lokasjon";
        case "VOYAGE_LOCATION_ARRIVAL_TIME":
            return "Seils stopp";
        case "PILOTAGE_LOCATION_DEPARTURE":
            return "Oppdrag start lokasjon";
        case "PILOTAGE_LOCATION_DEPARTURE_TIME":
            return "Oppdrag start";
        case "PILOTAGE_LOCATION_ARRIVAL":
            return "Oppdrag stopp lokasjon";
        case "PILOTAGE_LOCATION_ARRIVAL_TIME":
            return "Oppdrag stopp";
        case "VOYAGE_NUMBER_OF_PASSENGERS_ONBOARD":
            return "Antall personer";
        case "VOYAGE_ACTUAL_DRAUGHT":
            return "Aktuell DPG";

        case "NAVIGATOR_DETAILS":
            return "Navigatør";
        case "NAVIGATOR_NAME":
            return "Navn";
        case "NAVIGATOR_BIRTH_DATE":
            return "Fødselsdato";
        case "NAVIGATOR_NATIONALITY":
            return "Nasjonalitet";
        case "NAVIGATOR_HAS_VALID_PEC":
            return "Har gyldig FB";
        case "NAVIGATOR_PEC_NO":
            return "FB nr";
        case "NAVIGATOR_PEC_CLASS_1":
            return "FB klasse 1";
        case "NAVIGATOR_PEC_CLASS_2":
            return "FB klasse 2";
        case "NAVIGATOR_PEC_CLASS_3":
            return "FB klasse 3";
        case "NAVIGATOR_PEC_CLASS_CADET":
            return "FB kadett";
        case "NAVIGATOR_PEC_CLASS_IN_USE":
            return "FB i bruk";
        case "NAVIGATOR_FIRST_PEC_ISSUED_DATE_TIME":
            return "FB utstedt dato";
        case "NAVIGATOR_ON_DUTY":
            return "På vakt";
        case "NAVIGATOR_RESTING_TIME_MET":
            return "Hviletid overholdt";

        default:
            return `${systemName} (Mangler tittel)`;
    }
}

function toDate(data, ...fields) {
    fields.forEach((field) => {
        data = data.map((item) => (item = { ...item, [field]: new Date(item[field]) }));
    });
    return data;
}

function toActorDisplayName(data, ...fields) {
    fields.forEach((field) => {
        data = data.map((item) => (item = { ...item, [`${field}Nor`]: actorTypeToDisplayName(item[field]) }));
    });
    return data;
}

function getValidationError(prop, errors, isVisible, errorKey = "subItem", propKey = "systemName") {
    const hasKey = Object.prototype.hasOwnProperty.call(prop, propKey);
    const hasError = errors.some((error) => error[errorKey]?.systemName === prop[propKey]);
    return isVisible && hasKey && hasError;
}

export {
    getIncidentShipNameByJson,
    getIncidentTime,
    getIncidentMenuItems,
    getNavigatorName,
    getNavigatorCountry,
    getPilotName,
    getShipName,
    isIncidentCreatedByLoggedInPerson,
    getPositionNameAsync,
    isIncidentDraft,
    isIncidentCorrectionNeeded,
    isIncidentAwaitingProcessing,
    isIncidentBeingProcessed,
    isIncidentCorrectionCaseworker,
    isIncidentProcessingFinished,
    isIncidentArchived,
    getPropertyTitle,
    toDate,
    toActorDisplayName,
    getValidationError
};
